import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Search, Gavel, CreditCard, Truck, Building2, ShieldCheck, ArrowRight } from "lucide-react";

const STEPS = [
  { icon: <Search className="w-6 h-6" />, num: "01", title: "Discover", desc: "Search by state, city, or unit size. Every listing includes photos taken through the doorway and a full description from the storage facility." },
  { icon: <Gavel className="w-6 h-6" />, num: "02", title: "Bid Live", desc: "Place bids in increments set by the seller. If someone bids in the final 5 minutes, the auction auto-extends so you always have a chance to counter." },
  { icon: <CreditCard className="w-6 h-6" />, num: "03", title: "Pay Fast", desc: "Winners pay the final bid plus buyer's premium via Stripe within 24 hours. All payments are secure and receipts are emailed instantly." },
  { icon: <Truck className="w-6 h-6" />, num: "04", title: "Collect", desc: "Bring the receipt and a truck to the facility within 48 hours. Take everything — the whole unit is yours." },
];

const FAQ = [
  { q: "What is a storage unit auction?", a: "When a renter defaults on their storage payments for 60+ days, self-storage facilities are legally allowed to auction the contents to recover unpaid rent. Whatever is inside — furniture, tools, electronics, mystery boxes — goes to the highest bidder." },
  { q: "What is the buyer's premium?", a: "A percentage (typically 10–15%) added to the winning bid to cover platform fees. It's shown clearly on every listing before you bid." },
  { q: "Can I inspect the unit before bidding?", a: "No — that's the thrill. Sellers post photos from the doorway only. You bid on what you can see. Some units are goldmines; others aren't. That's the game." },
  { q: "What happens if I don't pay?", a: "Your account is suspended, the runner-up gets first refusal, and the unit is re-listed. Serious bidders only." },
];

export default function HowItWorksPage() {
  return (
    <div>
      <section className="border-b border-zinc-900 relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-2">The Playbook</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.9] max-w-4xl">
            How storage<br />auctions <span className="text-orange-500">actually work.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-400 leading-relaxed">
            Whether you&apos;re hunting for treasures or trying to move a stalled unit, here&apos;s the whole process — no fluff.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
          {STEPS.map((s) => (
            <div key={s.num} className="bg-zinc-950 border border-zinc-800 hover:border-orange-500 transition-colors p-8 flex gap-6">
              <div className="text-orange-500 shrink-0">{s.icon}</div>
              <div>
                <div className="font-display text-4xl font-black text-orange-500 tabular-nums leading-none">{s.num}</div>
                <h3 className="mt-3 font-display text-2xl font-bold uppercase tracking-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-zinc-900 bg-zinc-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-2">FAQ</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-12">
            Common Questions
          </h2>
          <div className="divide-y divide-zinc-800 border-t border-b border-zinc-800">
            {FAQ.map((f) => (
              <details key={f.q} className="group py-6">
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                  <span className="font-display text-lg font-bold uppercase tracking-tight text-white">{f.q}</span>
                  <span className="text-orange-500 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-zinc-900 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid md:grid-cols-2 gap-10">
          <div className="border border-zinc-800 p-8">
            <Building2 className="w-8 h-8 text-orange-500" />
            <h3 className="mt-4 font-display text-3xl font-bold uppercase tracking-tight">For Facilities</h3>
            <p className="mt-3 text-sm text-zinc-400 leading-relaxed">List defaulted units in minutes. Reach thousands of active bidders. Zero listing fees — we only take a small cut when the unit sells.</p>
            <Button asChild className="mt-6 rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-12 px-6">
              <Link to="/register">Start Selling <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
          <div className="border border-zinc-800 p-8">
            <ShieldCheck className="w-8 h-8 text-orange-500" />
            <h3 className="mt-4 font-display text-3xl font-bold uppercase tracking-tight">For Bidders</h3>
            <p className="mt-3 text-sm text-zinc-400 leading-relaxed">Discover units across the country. Watch your favorites, get outbid alerts in real-time, and never miss the auto-extend window.</p>
            <Button asChild className="mt-6 rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-12 px-6">
              <Link to="/browse">Browse Now <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
